import { Check, Copy, Mail, Phone, X } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { getContactDetails } from "../../constants/contact";
import { services } from "../../data/siteData";
import { formatCurrency } from "../../utils/formatters";

// ---------------------------------------------------------------------------
// BOOKING: call or email only
//
// Every Book button opens this panel with exactly two ways to schedule and pay
// for an appointment: call or email. There is no captcha. Instead, the phone
// number and email are stored encoded (constants/contact.js) and decoded only
// here, once a visitor has opened the panel, so spam bots scanning the page
// don't find them.
//
// Accessibility follows the W3C ARIA Authoring Practices "Dialog (Modal)"
// pattern: labelled by its visible title, focus moves inside on open, Tab and
// Shift+Tab stay inside, Escape or the backdrop closes it, and focus returns
// to the button that opened it.
// ---------------------------------------------------------------------------

const DURATION_LABELS = { "2h": "2 hr", "4h": "4 hr", "6h": "6 hr", "8h": "8 hr" };
const FOCUSABLE =
  'a[href], button:not([disabled]), select:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Pre-filled booking email. Per RFC 6068: percent-encode with
// encodeURIComponent (spaces become %20, never "+"), use CRLF line breaks,
// and keep the text plain ASCII for the widest mail-app compatibility.
export function buildBookingMailto(service, email) {
  const subject = service ? `Appointment request: ${service.name}` : "Appointment request";
  // e.g. "Wig Install ($75, 2 hr)". A plain "$" + digits keeps the link ASCII
  // whatever the visitor's language settings.
  const details = service
    ? `${service.name} (${[`$${service.price}`, DURATION_LABELS[service.duration]].filter(Boolean).join(", ")})`
    : "";
  const body = [
    "Hi Trinya,",
    "",
    "I'd like to book an appointment.",
    "",
    `Service: ${details}`,
    "Preferred date(s):",
    "Preferred time:",
    "Name:",
    "Phone:",
    "",
    "Thank you!",
  ].join("\r\n");
  return `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

// Lets the long address wrap after the name (not mid-word) on narrow phones.
function EmailAddress({ address }) {
  const at = address.indexOf("@");
  if (at < 1) return address;
  return (
    <>
      {address.slice(0, at)}
      <wbr />
      {address.slice(at)}
    </>
  );
}

function BookingPanel({ service, onClose }) {
  const titleId = useId();
  const descriptionId = useId();
  const pickerId = useId();
  const panelRef = useRef(null);
  const pressStartedOnBackdrop = useRef(false);
  // Decoded only now, once the visitor has opened the panel.
  const [contact] = useState(getContactDetails);
  const [copyState, setCopyState] = useState({ key: null, message: "" });
  // From a general Book button (nav, hero) the visitor picks the service here,
  // so the pre-filled email names it. Service-card Book buttons already know it.
  const [pickedServiceId, setPickedServiceId] = useState("");
  const chosenService = service || services.find((item) => String(item.id) === pickedServiceId) || null;

  // On open: remember the trigger, lock page scroll, focus Call.
  // On close (unmount): restore scroll and return focus to the trigger.
  useEffect(() => {
    const trigger = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panelRef.current?.querySelector(".booking-option__link")?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (trigger instanceof HTMLElement && trigger.isConnected) trigger.focus();
    };
  }, []);

  // Escape closes; Tab and Shift+Tab wrap around inside the panel.
  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab" || !panelRef.current) return;
      const items = [...panelRef.current.querySelectorAll(FOCUSABLE)];
      if (items.length === 0) return;
      const index = items.indexOf(document.activeElement);
      if (event.shiftKey && index <= 0) {
        event.preventDefault();
        items[items.length - 1].focus();
      } else if (!event.shiftKey && (index === -1 || index === items.length - 1)) {
        event.preventDefault();
        items[0].focus();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Clipboard writes must start synchronously inside the click (transient user
  // activation) and only work on HTTPS; failures give a clear next step.
  function copy(key, text, messages) {
    let attempt;
    try {
      attempt = navigator.clipboard.writeText(text);
    } catch (error) {
      attempt = Promise.reject(error);
    }
    Promise.resolve(attempt).then(
      () => setCopyState({ key, message: messages.copied }),
      () => setCopyState({ key: null, message: messages.failed }),
    );
  }

  const duration = service?.duration ? DURATION_LABELS[service.duration] : null;

  // tel: and mailto: links only work when the device has an app to handle
  // them. Without one (for example Gmail used in a web browser, or a computer
  // with no calling app) tapping the link does nothing at all. So every tap
  // also copies the number or address and says what to do next. The link still
  // opens the phone or email app whenever one is set up.
  const options = [
    {
      key: "phone",
      label: "Call",
      href: contact.phoneTel,
      Icon: Phone,
      value: contact.phoneDisplay,
      copyText: contact.phoneDisplay,
      copyLabel: "phone number",
      onCopy: {
        copied: "Phone number copied.",
        failed: "Couldn't copy the phone number. Select it and copy it instead.",
      },
      onOpen: {
        copied: "If no calling app opened, the number is copied. Dial it from your phone.",
        failed: "If no calling app opened, dial the number above from your phone.",
      },
    },
    {
      key: "email",
      label: "Email",
      href: buildBookingMailto(chosenService, contact.email),
      Icon: Mail,
      value: <EmailAddress address={contact.email} />,
      copyText: contact.email,
      copyLabel: "email address",
      onCopy: {
        copied: "Email address copied.",
        failed: "Couldn't copy the email address. Select it and copy it instead.",
      },
      onOpen: {
        copied: "If your email app didn't open, the address is copied. Paste it into any email.",
        failed: "If your email app didn't open, copy the address above into any email.",
      },
    },
  ];

  return (
    <div
      className="modal-backdrop"
      onPointerDown={(event) => {
        pressStartedOnBackdrop.current = event.target === event.currentTarget;
      }}
      onClick={(event) => {
        // Only a press that starts and ends on the backdrop closes the panel, so
        // drag-selecting the phone number or email never closes it by accident.
        if (pressStartedOnBackdrop.current && event.target === event.currentTarget) onClose();
        pressStartedOnBackdrop.current = false;
      }}
    >
      <div
        ref={panelRef}
        className="modal-card booking-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        tabIndex={-1}
      >
        <h2 id={titleId} className="modal-title booking-panel__title">
          {service ? service.name : "Book an appointment"}
        </h2>
        {service ? (
          <p className="booking-panel__meta">
            <span>{formatCurrency(service.price)}</span>
            {duration ? <span>{duration}</span> : null}
          </p>
        ) : null}

        <p id={descriptionId} className="booking-panel__lede">
          Call or email to schedule your appointment and arrange payment.
        </p>

        {!service ? (
          <div className="booking-service-picker">
            <label htmlFor={pickerId} className="booking-service-picker__label">
              Service (added to your email)
            </label>
            <select
              id={pickerId}
              className="booking-input booking-service-picker__select"
              value={pickedServiceId}
              onChange={(event) => setPickedServiceId(event.target.value)}
            >
              <option value="">Choose a service (optional)</option>
              {services.map((item) => (
                <option key={item.id} value={String(item.id)}>
                  {`${item.name} ($${item.price})`}
                </option>
              ))}
            </select>
            {chosenService ? (
              <p className="booking-panel__meta">
                <span>{`$${chosenService.price}`}</span>
                {DURATION_LABELS[chosenService.duration] ? <span>{DURATION_LABELS[chosenService.duration]}</span> : null}
              </p>
            ) : null}
          </div>
        ) : null}

        <ul className="booking-options">
          {options.map(({ key, label, href, Icon, value, copyText, copyLabel, onCopy, onOpen }) => (
            <li key={key} className="booking-option">
              <a className="booking-option__link" href={href} onClick={() => copy(key, copyText, onOpen)}>
                <span className="booking-option__icon" aria-hidden="true">
                  <Icon size={20} />
                </span>
                <span className="booking-option__text">
                  <span className="booking-option__label">{label}</span>{" "}
                  <span className="booking-option__value">{value}</span>
                </span>
              </a>
              <button
                type="button"
                className="booking-option__copy"
                data-copy={key}
                aria-label={`Copy ${copyLabel}`}
                title={`Copy ${copyLabel}`}
                onClick={() => copy(key, copyText, onCopy)}
              >
                {copyState.key === key ? <Check size={18} aria-hidden="true" /> : <Copy size={18} aria-hidden="true" />}
              </button>
            </li>
          ))}
        </ul>

        <p className="booking-panel__status" role="status" aria-live="polite">
          {copyState.message}
        </p>

        <button type="button" className="booking-panel__close" onClick={onClose} aria-label="Close">
          <X size={20} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

export function BookingModal({ isOpen, onClose, preselectedService }) {
  if (!isOpen) return null;
  return <BookingPanel service={preselectedService} onClose={onClose} />;
}
